import { useEffect, useMemo } from 'react';
import parse from 'html-react-parser';
import { useSearchParams } from 'react-router-dom';
import { useSessionStorage } from 'usehooks-ts';
import { skipToken } from '@reduxjs/toolkit/query';
import {
  MARK_VISIBILITY_GROUP_KEY,
  SEARCH_RESULT,
  VERSION_CONTENT_CONTAINER_ID,
  markIdUrlParam,
  searchKeywordUrlParam,
} from '../../shared/constants';
import { useAppDispatch, useAppSelector } from '../../redux/hooks';
import {
  selectActiveVersion,
  setActiveNote,
  setShowNotePopoverOpened,
  setVersionContentProcessing,
} from '../../redux/store/content/slice';
import { NoteVisibilityType } from '../../shared/enums';
import { preventApplicationReloadAndOpenInternalRoute } from '../../shared/utils';
import { VisibilityGroupString } from '../notes/types';
import { contentReferenceClass } from './functions/nodes';
import { scrollMarkIntoView } from '../notes/functions';
import useMakeMarksClickable from './hooks.ts/useMakeMarksClickable';
import useMakeAnchorButtonsClickable from './hooks.ts/useMakeAnchorButtonsClickable';
import { useGetApiNotesByVersionIdQuery } from '../../redux/store/api/api';
import useGetVersionContent from './hooks.ts/useGetVersionContent';
import Loader from '../loader/Loader';
import VersionAlerts from './VersionAlerts';

function VersionContent() {
  const version = useAppSelector(selectActiveVersion);
  const dispatch = useAppDispatch();

  const { content, isProcessing } = useGetVersionContent();
  const [, setActiveMarkVisibilityGroup] =
    useSessionStorage<VisibilityGroupString | null>(
      MARK_VISIBILITY_GROUP_KEY,
      null,
    );
  const [searchParams, setSearchParams] = useSearchParams();
  const isSearchcontent = useMemo(
    () => searchParams.get(searchKeywordUrlParam) !== null,
    [searchParams],
  );
  const markIdFromBookmark: string | null = searchParams.get(markIdUrlParam);

  const { data: noteData } = useGetApiNotesByVersionIdQuery(
    version.id
      ? {
          versionId: version.id,
        }
      : skipToken,
    { refetchOnMountOrArgChange: true },
  );

  useMakeMarksClickable();
  useMakeAnchorButtonsClickable();

  useEffect(() => {
    dispatch(setVersionContentProcessing(isProcessing));
  }, [isProcessing]);

  useEffect(() => {
    // Set focus on article title
    const articleTitleElement = document
      .getElementsByClassName('article-content')[0]
      .getElementsByClassName('card-title')[0]
      .getElementsByTagName('h1')[0];

    articleTitleElement.focus();
  }, [version]);

  useEffect(() => {
    // Prevent full application reload when opening the link of an anchor tag
    const contentRoot = document.getElementById(VERSION_CONTENT_CONTAINER_ID);
    const anchorTags: NodeListOf<HTMLAnchorElement> | undefined =
      contentRoot?.querySelectorAll(`a[class='${contentReferenceClass}']`);

    anchorTags?.forEach((a) => {
      ['click', 'onkeydown'].forEach((evt) =>
        a.addEventListener(
          evt,
          (e) => {
            preventApplicationReloadAndOpenInternalRoute(e, a);
          },
          false,
        ),
      );
    });

    return () => {
      anchorTags?.forEach((a) => {
        ['click', 'onkeydown'].forEach((evt) =>
          a.removeEventListener(
            evt,
            (e) => {
              preventApplicationReloadAndOpenInternalRoute(e, a);
            },
            false,
          ),
        );
      });
    };
  }, [content]);

  useEffect(() => {
    if (markIdFromBookmark) {
      const noteOfMarkIdInUrl = [
        ...(noteData?.resultObject?.userNotes || []),
        ...(noteData?.resultObject?.userGroupNotes || []),
        ...(noteData?.resultObject?.generalNotes || []),
      ].find((n) => n.mark?.id === markIdFromBookmark);

      if (noteOfMarkIdInUrl) {
        setActiveMarkVisibilityGroup(
          NoteVisibilityType[
            noteOfMarkIdInUrl.visibility || 0
          ] as VisibilityGroupString,
        );
        dispatch(setActiveNote(noteOfMarkIdInUrl));
        dispatch(setShowNotePopoverOpened(true));
        scrollMarkIntoView(noteOfMarkIdInUrl);
        searchParams.delete(markIdUrlParam);
        setSearchParams(searchParams);
      }
    }
  }, [markIdFromBookmark, content, noteData]);

  useEffect(() => {
    // Add spans with highlight border to search results
    if (!isSearchcontent) {
      return;
    }

    setTimeout(() => {
      const focusEl = document.getElementsByClassName(SEARCH_RESULT)[0];

      if (focusEl) {
        focusEl.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    });
  }, [isSearchcontent]);

  return (
    <>
      {isProcessing && <Loader />}

      {!isProcessing && (
        <>
          <VersionAlerts />

          <div
            id={VERSION_CONTENT_CONTAINER_ID}
            className='mt-2 pt-1 version-content'>
            {parse(content)}
          </div>
        </>
      )}
    </>
  );
}

export default VersionContent;
